import { Injectable } from '@angular/core';
import { ResourceVm } from 'qp-ui-interface';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ContentType } from '../../enums/contentType';
import { PlayerState } from '../../enums/player-states';
import { IPlayerMeta } from '../../interfaces/player.interface';
import { PlaybackService } from '../../services/playback.service';
import { TabService } from '../../services/tabs/tab.service';

export enum PlayerEventType {
  META = 'meta',
  STATE = 'state',
  VIDEO_AVAILABLE = 'videoAvailable',
  VIDEO_COMPLETED = 'videoCompleted',
  FORCE_DESTROY = 'forceDestroy',
  FORCE_RESTORE = 'forceRestore',
}

export interface PlayerEvent {
  type: PlayerEventType;
  data: IPlayerMeta | PlayerState | boolean | null;
  tabId?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SimplePlayerService {
  private _currentResource: ResourceVm | null;
  private _lastEvent: PlayerEvent | null = null;
  playerEvents$ = new BehaviorSubject<PlayerEvent>({
    type: PlayerEventType.META,
    data: null,
  });

  get currentResource(): ResourceVm | null {
    return this._currentResource;
  }

  private dialogState$ = new Subject<boolean>();

  constructor(
    private playbackService: PlaybackService,
    private tabService: TabService
  ) {}

  handleResource(resource: ResourceVm) {
    if (
      this.currentResource?.id !== resource?.id ||
      (resource?.containerId !== this.currentResource?.containerId &&
        resource?.containerName &&
        resource?.containerName['en'] !== 'hero')
    ) {
      const videoUrl = resource.ape ? resource.apUrl?.dash : null;
      const isPlayable =
        Boolean(videoUrl) &&
        resource.contentType?.toLowerCase() !== ContentType.LIVE_CHANNEL;
      this._currentResource = resource;

      const metaEvent = {
        type: PlayerEventType.META,
        data: {
          imageUrl: resource.bannerImage,
          videoUrl,
          isPlayable,
        },
      };
      this._lastEvent = metaEvent;
      this.playerEvents$.next(metaEvent);
      const videoAvailableEvent = {
        type: PlayerEventType.VIDEO_AVAILABLE,
        data: isPlayable,
        tabId: this.tabService.currentTabId,
      };
      this.playerEvents$.next(videoAvailableEvent);
    }
  }

  resetEvent() {
    this._currentResource = null;
    this._lastEvent = null;
    this.playerEvents$.next({
      type: PlayerEventType.META,
      data: null,
    });
  }

  updatePlayerState(state: PlayerState) {
    const event = {
      type: PlayerEventType.STATE,
      data: state,
    };
    this._lastEvent = event;
    this.playerEvents$.next(event);
  }

  getLastEvent(): PlayerEvent | null {
    return this._lastEvent;
  }

  notifyVideoCompleted() {
    this.playerEvents$.next({
      type: PlayerEventType.VIDEO_COMPLETED,
      data: true,
      tabId: this.tabService.currentTabId,
    });
  }

  async initializePlayer(video: HTMLVideoElement) {
    return await this.playbackService.getSimplePlayer(
      this.currentResource,
      video
    );
  }

  get dialogStateChanges$(): Observable<boolean> {
    return this.dialogState$.asObservable();
  }

  setDialogState(state: boolean) {
    this.dialogState$.next(state);
  }

  forceDestroyPreview() {
    this.playerEvents$.next({
      type: PlayerEventType.FORCE_DESTROY,
      data: null,
      tabId: this.tabService.currentTabId,
    });
    this._lastEvent = null;
  }

  forceRestorePreview() {
    this.playerEvents$.next({
      type: PlayerEventType.FORCE_RESTORE,
      data: null,
    });
    const currentResource = this._currentResource;
    this._currentResource = null;
    this.handleResource(currentResource);
  }
}
